/**
 * The alert queue — what needs renewing, worst first.
 *
 * Acknowledging is not dismissing. The alert stays open until its cause is gone, but
 * stops shouting: "renewal filed, expect it Thursday" is exactly the state a compliance
 * list has to be able to hold. A list that cannot hold it is one the office stops
 * reading, which defeats the point.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Card, ErrorNotice, PageHeader, Spinner, Stat } from '@/components/ui';
import { useI18n } from '@/i18n';
import { Modal } from '@/components/Modal';
import { MobileList } from '@/components/MobileList';
import { DataToolbar, SortableTh } from '@/components/table';
import { api, ApiError, type Alert } from '@/lib/api';
import { useTableState, type SortOption } from '@/lib/table';
import { ago, dateShort } from '@/lib/format';
import { useAlertText } from '@/lib/useAlertText';
import { useAlertKind, useLabel } from '@/lib/useLabel';

function AlertRow({ alert, onAcknowledge }: { alert: Alert; onAcknowledge: (a: Alert) => void }) {
  const { t } = useI18n();
  const L = useLabel();
  const kindLabel = useAlertKind();
  const text = useAlertText();
  const spec = L('severity', alert.severity);
  const { title, body } = text(alert);

  return (
    <tr className={`hover:bg-surface-alt ${alert.acknowledgedAt ? 'opacity-60' : ''}`}>
      <td className="td">
        <Badge tone={spec.tone}>{spec.label}</Badge>
      </td>
      <td className="td">
        <span className="font-semibold">{title}</span>
        {body ? <div className="text-xs text-muted">{body}</div> : null}
        {alert.acknowledgedAt ? (
          <div className="text-xs text-primary mt-1">
            {t('alerts.acknowledgedBy', {
              name: alert.acknowledgedByName ?? '—',
              note: alert.acknowledgedNote ?? '',
            })}
          </div>
        ) : null}
      </td>
      <td className="td text-sm whitespace-nowrap">
        {alert.userId ? (
          <Link to={`/employees/${alert.userId}`} className="text-primary hover:underline">
            {alert.userName}
          </Link>
        ) : (
          <span className="text-subtle">{t('common.company')}</span>
        )}
      </td>
      <td className="td text-xs text-muted whitespace-nowrap">
        {kindLabel(alert.kind)}
      </td>
      <td className="td tabular text-sm whitespace-nowrap">
        {alert.dueOn ? dateShort(alert.dueOn) : '—'}
      </td>
      <td className="td text-xs text-muted whitespace-nowrap">{ago(alert.createdAt)}</td>
      <td className="td text-end">
        {alert.acknowledgedAt ? (
          <span className="text-xs text-subtle">{t('alerts.acknowledged')}</span>
        ) : (
          <button
            type="button"
            className="td-action text-primary hover:underline"
            onClick={() => onAcknowledge(alert)}
          >
            {t('alerts.acknowledge')}
          </button>
        )}
      </td>
    </tr>
  );
}

const ALERT_SORTS: SortOption<Alert>[] = [
  // Worst first, then soonest — the same order the backend's compareAlerts uses, so the
  // default view here matches what the nightly job considers urgent.
  { key: 'severity', labelKey: 'alerts.severity', value: (a) => SEVERITY_RANK[a.severity] ?? 9 },
  { key: 'due', labelKey: 'alerts.dueOn', value: (a) => a.dueOn },
  { key: 'person', labelKey: 'alerts.concerns', value: (a) => a.userName },
  { key: 'detected', labelKey: 'alerts.detectedAt', value: (a) => a.createdAt, defaultDirection: 'desc' },
];

const SEVERITY_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };

export function AlertsPage() {
  const { t } = useI18n();
  const L = useLabel();
  const kindLabel = useAlertKind();
  const alertText = useAlertText();
  const queryClient = useQueryClient();
  const [includeAcknowledged, setIncludeAcknowledged] = useState(false);
  const [acknowledging, setAcknowledging] = useState<Alert | null>(null);
  const [note, setNote] = useState('');

  const alerts = useQuery({
    queryKey: ['alerts', includeAcknowledged],
    queryFn: () => api.alerts(includeAcknowledged),
  });

  const acknowledge = useMutation({
    mutationFn: () => api.acknowledgeAlert(acknowledging!.id, note.trim()),
    onSuccess: async () => {
      setAcknowledging(null);
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const rescan = useMutation({
    mutationFn: api.rescanAlerts,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  });

  // Search the facts, not the rendered sentence: a worker's name and a document's name
  // are what people type, and searching the rendered text would also mean the results
  // changed with the interface language.
  const table = useTableState<Alert>({
    rows: alerts.data?.alerts ?? [],
    search: (a) => [
      a.subjectLabel,
      a.holderName,
      a.userName,
      a.title,
      kindLabel(a.kind),
      L('severity', a.severity).label,
    ],
    sorts: ALERT_SORTS,
    defaultSortKey: 'severity',
    storageKey: 'alerts',
  });
  const rows = table.rows;
  const counts = alerts.data?.counts ?? { critical: 0, warning: 0, info: 0 };

  return (
    <>
      <PageHeader
        title={t('alerts.title')}
        subtitle={t('alerts.subtitle')}
        actions={
          <button
            type="button"
            className="btn-secondary"
            disabled={rescan.isPending}
            onClick={() => rescan.mutate()}
          >
            {rescan.isPending ? t('alerts.rescanning') : t('alerts.rescan')}
          </button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat
          label={t('alerts.critical')}
          value={counts.critical}
          tone={counts.critical > 0 ? 'danger' : 'success'}
          hint={t('alerts.criticalHint')}
        />
        <Stat
          label={t('alerts.warning')}
          value={counts.warning}
          tone={counts.warning > 0 ? 'warning' : 'success'}
          hint={t('alerts.warningHint')}
        />
        <Stat label={t('alerts.info')} value={counts.info} />
      </div>

      <Modal
        open={acknowledging !== null}
        onClose={() => setAcknowledging(null)}
        size="sm"
        title={t('alerts.acknowledge')}
        description={acknowledging ? alertText(acknowledging).title : undefined}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={note.trim().length < 3 || acknowledge.isPending}
              onClick={() => acknowledge.mutate()}
            >
              {acknowledge.isPending ? t('common.saving') : t('common.confirm')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setAcknowledging(null)}
            >
              {t('common.cancel')}
            </button>

            {acknowledge.error ? (
              <p className="text-sm text-danger ms-auto">
                {acknowledge.error instanceof ApiError
                  ? acknowledge.error.message
                  : t('common.saveError')}
              </p>
            ) : null}
          </>
        }
      >
        <p className="text-xs text-muted mb-3 leading-relaxed">
          {t('alerts.acknowledgeExplainer')}
        </p>

        <textarea
          className="input h-24 py-2 resize-none"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('alerts.notePlaceholder')}
        />
      </Modal>

      <DataToolbar
        state={table}
        searchPlaceholder={t('alerts.searchPlaceholder')}
        countOne="alerts.countOne"
        countMany="alerts.countMany"
        extra={
          <label className="flex items-center gap-2 text-sm h-9 whitespace-nowrap">
            <input
              type="checkbox"
              checked={includeAcknowledged}
              onChange={(e) => setIncludeAcknowledged(e.target.checked)}
            />
            {t('alerts.showAcknowledged')}
          </label>
        }
      />

      {alerts.isLoading ? (
        <Spinner />
      ) : alerts.error ? (
        <ErrorNotice error={alerts.error} />
      ) : rows.length === 0 ? (
        <Card>
          <p className="text-sm text-muted">
            {includeAcknowledged
              ? t('alerts.noneOpen')
              : t('alerts.noneToHandle')}
          </p>
          {!includeAcknowledged ? (
            <label className="flex items-center gap-2 text-sm mt-3">
              <input
                type="checkbox"
                checked={includeAcknowledged}
                onChange={(e) => setIncludeAcknowledged(e.target.checked)}
              />
              {t('alerts.showAcknowledged')}
            </label>
          ) : null}
        </Card>
      ) : (
        <Card padded={false}>
          {/* The filter belongs on the thing it filters, not in a card of its own above
              it — one checkbox does not need its own panel. */}
          <div className="lg:hidden p-2">
            <MobileList<Alert>
              rows={rows}
              keyOf={(a) => a.id}
              title={(a) => alertText(a).title}
              subtitle={(a) => alertText(a).body || kindLabel(a.kind)}
              badges={(a) => (
                <>
                  <Badge tone={L('severity', a.severity).tone}>
                    {L('severity', a.severity).label}
                  </Badge>
                  {a.acknowledgedAt ? (
                    <Badge tone="neutral">{t('alerts.acknowledged')}</Badge>
                  ) : null}
                </>
              )}
              fields={[
                { label: t('alerts.concerns'), value: (a) => a.userName ?? t('common.company') },
                { label: t('alerts.kind'), value: (a) => kindLabel(a.kind) },
                { label: t('alerts.dueOn'), value: (a) => (a.dueOn ? dateShort(a.dueOn) : null) },
                { label: t('alerts.detectedAt'), value: (a) => ago(a.createdAt) },
              ]}
              action={(a) =>
                a.acknowledgedAt ? null : (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                      setAcknowledging(a);
                      setNote('');
                    }}
                  >
                    {t('alerts.acknowledge')}
                  </button>
                )
              }
            />
          </div>

          <div className="table-scroll max-lg:hidden">
            <table className="table-wide">
              <thead>
                <tr>
                  <SortableTh state={table} sortKey="severity">
                    {t('alerts.severity')}
                  </SortableTh>
                  <th className="th w-[34%]">{t('alerts.alert')}</th>
                  <SortableTh state={table} sortKey="person">
                    {t('alerts.concerns')}
                  </SortableTh>
                  <th className="th">{t('alerts.kind')}</th>
                  <SortableTh state={table} sortKey="due">
                    {t('alerts.dueOn')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="detected">
                    {t('alerts.detectedAt')}
                  </SortableTh>
                  <th className="th" />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <AlertRow
                    key={a.id}
                    alert={a}
                    onAcknowledge={(alert) => {
                      setAcknowledging(alert);
                      setNote('');
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
